import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Authorization header if access token cached (cross-origin fallback)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sos_access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Silent token refresh on 401 — keeps users signed in for up to 30 days as
// long as the refresh_token cookie is still alive. Avoids the "site shows
// login page" / "vessels list is empty" symptoms after a long idle.
let refreshPromise = null;
const AUTH_EXEMPT_PATHS = ["/auth/login", "/auth/refresh", "/auth/auto-login", "/auth/me", "/auth/logout"];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthCall = AUTH_EXEMPT_PATHS.some((p) => url.includes(p));
    if (status !== 401 || original._retried || isAuthCall) {
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      if (!refreshPromise) {
        refreshPromise = axios
          .post(`${API}/auth/refresh`, {}, { withCredentials: true })
          .finally(() => {
            // Reset so a future 401 cycle can refresh again.
            setTimeout(() => { refreshPromise = null; }, 0);
          });
      }
      const { data } = await refreshPromise;
      if (data?.access_token) {
        localStorage.setItem("sos_access_token", data.access_token);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${data.access_token}`;
      }
      return api(original);
    } catch (e) {
      // Refresh genuinely failed — surface a clean session-expired UX.
      localStorage.removeItem("sos_access_token");
      if (typeof window !== "undefined") {
        const here = window.location.pathname + window.location.search;
        if (!here.startsWith("/login")) {
          // Avoid redirect loops on the login page itself.
          window.location.replace(`/login?expired=1&next=${encodeURIComponent(here)}`);
        }
      }
      return Promise.reject(e);
    }
  }
);

/**
 * Upload a file, automatically switching to chunked mode for large files so
 * the production edge/proxy body-size cap can't kill the request. Returns the
 * same payload shape as POST /upload: {path, preview_path, preview_paths, ...}.
 */
const CHUNK_SIZE = 4 * 1024 * 1024;

export async function uploadFileSmart(file, { onProgress } = {}) {
  if (file.size <= CHUNK_SIZE) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd);
    onProgress?.(1);
    return data;
  }
  const uploadId =
    (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  const total = Math.ceil(file.size / CHUNK_SIZE);
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", String(i));
    fd.append("file", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE), file.name);
    await api.post("/upload/chunk", fd);
    onProgress?.((i + 1) / (total + 1));
  }
  const { data } = await api.post("/upload/chunk/complete", {
    upload_id: uploadId,
    filename: file.name,
    total_chunks: total,
  });
  onProgress?.(1);
  return data;
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") {
    // Defensive: when an upstream (Cloudflare, NGINX, etc.) intercepts the
    // request, the response body is usually an HTML error page. Surfacing
    // raw HTML / CDN copy to end users is jarring and unhelpful — fall
    // back to a clean message instead.
    const looksLikeCdnError =
      detail.startsWith("<") ||
      /<!doctype/i.test(detail) ||
      /cloudflare/i.test(detail) ||
      detail.length > 240;
    if (looksLikeCdnError) return "We can't reach the server right now. Please try again in a moment.";
    return detail;
  }
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Build a user-safe error message from a caught axios error.
 *
 * Maps server-side / CDN / network failures (Cloudflare 5xx, NGINX bad
 * gateway, dropped connections) to one consistent "we can't reach the
 * server" line so a client/customer never sees infrastructure copy like
 * "The origin web server sent a response that Cloudflare could not parse".
 *
 * Falls back to the FastAPI `detail` payload for normal application errors
 * (4xx with JSON), which is the case for things like "Invalid email or
 * password".
 */
export function friendlyAxiosError(e) {
  const status = e?.response?.status;
  const data = e?.response?.data;
  const isHtmlString = typeof data === "string" && (data.startsWith("<") || /<!doctype/i.test(data));
  const cdnLike = status >= 500 || status === 0 || isHtmlString;
  // Log the underlying failure to DevTools before we collapse it into
  // a friendly message. Support requested this so a real HTTP status
  // and body are visible when a customer reports "can't reach the
  // server" — without changing what the end user sees.
  console.error("[api] request failed", {
    url: e?.config?.url,
    method: e?.config?.method,
    status: status ?? "(no response)",
    statusText: e?.response?.statusText,
    responseBody: typeof data === "string" ? data.slice(0, 500) : data,
    message: e?.message,
  });
  // Network error or request never returned a response (CORS preflight
  // fail, DNS, offline, dropped) — axios throws without `response`.
  if (!e?.response) {
    return "We can't reach the server right now. Please check your connection and try again.";
  }
  if (cdnLike) {
    return "We can't reach the server right now. Please try again in a moment.";
  }
  // Normal application error — use the FastAPI `detail` field.
  return formatApiError(data?.detail) || "Something went wrong. Please try again.";
}

export default api;
